import { MISSION_STATUS_ENUM } from '../../shared/constants'
import { getMetricResultForMissions } from '../../Reducers/MetricResults/selectors'
import {
  calculateHardCost,
  calculateData,
  convertGroupedMissionToDto,
} from '../StatsCityPlan/util'
import {
  dropTimeZoneFromDate,
  getWeekNumberAndYear,
} from '../../shared/libraries/utils/date'
import {
  countArrayValues,
  averageArrayValues,
} from '../../Components/Stats/util'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import { useMemo } from 'react'

export const zeroOutMissingValues = function(arr, length = arr.length) {
  // Let's first standardise the arrays, this mutates them
  for (let i = 0; i < length; i++) {
    if (arr[i] === undefined) arr[i] = 0
  }
}

export const makeValuesCumulative = function(arr) {
  arr.reduce((cum, cur, i) => (arr[i] = cum + cur), 0)
}

const weekNumSort = function(a, b) {
  let [aYear, aWeekNum] = a.split('-'),
    [bYear, bWeekNum] = b.split('-')
  aYear = Number(aYear)
  bYear = Number(bYear)
  aWeekNum = Number(aWeekNum)
  bWeekNum = Number(bWeekNum)
  if (aYear < bYear) return -1
  if (bYear < aYear) return 1
  if (aWeekNum < bWeekNum) return -1
  if (bWeekNum < aWeekNum) return 1
  return 0
}

const formatItemAndConvertCVRToPercent = value => {
  const cvrKey = Object.keys(value).filter(key => key.includes('cvr'))[0]
  return cvrKey ? { ...value, [cvrKey]: value[cvrKey] * 100 } : value
}

export const formatData = campaign => {
  const missions = campaign.cityPlans.nodes.reduce(
    (acc, cityPlan) => [
      ...acc,
      ...normalizeGraphQLConnection(cityPlan.missions).map(mission => ({
        ...mission,
        cityPlanName: cityPlan.city.name,
        cityPlanId: cityPlan.id,
      })),
    ],
    [],
  )
  const cityPlans = campaign.cityPlans.nodes
  const metrics = campaign.metrics.nodes

  const campaignData = campaign

  if (!campaignData) {
    return {
      metrics: [],
      missions: [],
      metricResultsHashmap: {},
      cityTableData: {
        legend: [],
        data: [],
      },
      weeksTableData: {
        legend: [],
        data: [],
      },
      tagTableData: {
        legend: [],
        data: [],
      },
    }
  }

  // Filter out the non complete missions
  const completedMissions = missions.filter(filterCompletedMissions)

  const totalFlyersDistributed = missions.reduce(
    (acc, mission) => acc + mission.totalFlyersDistributed,
    0,
  )
  const metric = metrics[0]

  const bulletCharts = metric
    ? (() => {
        const { id, name } = metric
        const {
          totalFlyersDistributed,
          totalMetricResults,
        } = getMetricResultForMissions(missions)

        const totalConversions = totalMetricResults[id]
        const conversionRate =
          totalFlyersDistributed > 0
            ? (totalConversions / totalFlyersDistributed) * 100 // Multiplied by 100 for a percentage value
            : null
        const targetConversionRate = metric.targetConversionRate * 100 // Multiplied by 100 for a percentage value
        const missionCost = calculateHardCost(completedMissions, cityPlans)
        const costPerMetric =
          missionCost && totalConversions
            ? missionCost / totalConversions / 100 // to dollars
            : null
        const targetCostPerMetric = metric.targetCostPerMetric / 100 // to dollars

        return {
          metricName: name,
          totalMissions: completedMissions.length,
          totalMissionsWithResults: totalMetricResults[id + '-total'],
          targetCostPerMetric,
          targetConversionRate,
          conversionRate,
          costPerMetric,
        }
      })()
    : null

  const cityTableData = useMemo(
    () =>
      calculateData({
        primaryLegend: {
          label: 'City',
          key: 'city',
        },
        groupedMissions: cityPlans.map(cityPlan => {
          const { city } = cityPlan
          const missions = normalizeGraphQLConnection(cityPlan.missions).map(
            mission => ({ ...mission, cityPlanId: cityPlan.id }),
          )
          const completedMissions = normalizeGraphQLConnection(
            cityPlan.missions,
          )
            .filter(filterCompletedMissions)
            .map(mission => ({ ...mission, cityPlanId: cityPlan.id }))

          return {
            primaryLegendLabel: city.name,
            ...getMetricResultForMissions(missions),
            totalMissions: completedMissions.length,
            hardCost: calculateHardCost(completedMissions, cityPlans),
          }
        }),
        metrics,
      }),
    [missions, metrics],
  )

  const tagTableData = useMemo(() => {
    const noTagLabel = 'No Tag'
    const tags = Object.keys(
      missions.reduce((acc, element) => {
        const tag = element.flyerCodeV2 ? element.flyerCodeV2.tag : null
        const item = { ...element.flyerCodeV2 }
        const tagName = !tag ? noTagLabel : tag
        if (!acc[tagName]) {
          acc[tagName] = []
        }
        acc[tagName].push(item)
        return acc
      }, {}),
    )

    return calculateData({
      primaryLegend: {
        label: 'AB Test',
        key: 'tag',
      },
      groupedMissions: tags.map(tag => {
        const tagName = tag || noTagLabel
        const completedMissionsData = missions.filter(
          mission =>
            ((mission.flyerCodeV2 && mission.flyerCodeV2.tag) || noTagLabel) ===
              tagName && filterCompletedMissions(mission),
        )
        const missionsData = missions.filter(
          mission =>
            ((mission.flyerCodeV2 && mission.flyerCodeV2.tag) || noTagLabel) ===
            tagName,
        )
        return {
          primaryLegendLabel: tag,
          ...getMetricResultForMissions(missionsData),
          totalMissions: completedMissionsData.length,
          hardCost: calculateHardCost(completedMissionsData, cityPlans),
        }
      }),
      metrics,
    })
  }, [missions, metrics])

  // Grab total weeks from the next calculation
  const [totalWeeks, weeksTableData] = useMemo(() => {
    const missionsGroupedByWeekNumber = {}
    missions.forEach(mission => {
      // Needs to be converted to a date but a date that would be relative
      // to the city the mission is in.
      const date = new Date(dropTimeZoneFromDate(mission.dateTime))
      const { weekNumber, year } = getWeekNumberAndYear(date)
      missionsGroupedByWeekNumber[year + '-' + weekNumber] =
        missionsGroupedByWeekNumber[year + '-' + weekNumber] || []
      missionsGroupedByWeekNumber[year + '-' + weekNumber].push(mission)
    })

    const rosterWeekData = Object.keys(missionsGroupedByWeekNumber).sort(
      weekNumSort,
    )

    return [
      rosterWeekData.length,
      calculateData({
        primaryLegend: { label: 'Week', key: 'week' },
        groupedMissions: rosterWeekData.map(weekNum => {
          const missions = missionsGroupedByWeekNumber[weekNum]
          // missionIds is an array of ids
          const completedMissions = missionsGroupedByWeekNumber[weekNum].filter(
            filterCompletedMissions,
          )
          return {
            primaryLegendLabel: 'W' + weekNum.split('-')[1],
            ...getMetricResultForMissions(missions),
            totalMissions: completedMissions.length,
            hardCost: calculateHardCost(completedMissions, cityPlans),
          }
        }),
        metrics,
      }),
    ]
  }, [missions, metrics])

  const {
    evolutionOverTheTimeChartData,
    evolutionPerCityChartData,
    chartDataHash,
    chartMetrics,
    chartWeeks,
  } = useMemo(() => {
    const defaultData = {
      flyersDistributed: 0,
      hardCost: 0,
      missions: 0,
    }

    metrics.forEach(item => {
      defaultData[item.name] = 0
      defaultData[item.name + '-cps'] = 0
      defaultData[item.name + '-cvr'] = 0
    })

    const { data, rawCities, rawWeeks } = completedMissions.reduce(
      (acc, item) => {
        const { cityPlanName: city, dateTime } = item
        const date = new Date(dropTimeZoneFromDate(dateTime))
        const { weekNumber, year } = getWeekNumberAndYear(date)
        const yearAndWeek = `${year}-W${
          weekNumber < 10 ? '0' + weekNumber : weekNumber
        }`
        const chartId = `${city}|${yearAndWeek}`

        acc.data[chartId] = acc.data[chartId]
          ? [...acc.data[chartId], item]
          : [item]

        acc.rawCities[city] = { ...defaultData, city, week: yearAndWeek }
        acc.rawWeeks[yearAndWeek] = {
          ...defaultData,
          city,
          week: yearAndWeek,
        }

        return acc
      },
      { data: {}, rawCities: {}, rawWeeks: {} },
    )

    const cities = Object.keys(rawCities).reduce((acc, key) => {
      acc[key] = { ...rawWeeks }
      return acc
    }, {})

    const weeks = Object.keys(rawWeeks).reduce((acc, key) => {
      acc[key] = { ...rawCities }
      return acc
    }, {})

    const chartData = Object.entries(data).map(([key, value]) =>
      convertGroupedMissionToDto(
        'chartId',
        metrics,
        {
          primaryLegendLabel: key,
          ...getMetricResultForMissions(value),
          totalMissions: value.length,
          hardCost: calculateHardCost(value, cityPlans),
        },
        { prefix: '', metricIdProp: 'name' },
      ),
    )

    const cityWeekMatrix = chartData.reduce(
      (acc, item) => {
        const [city, week] = item.chartId.split('|')
        acc.weeks[week][city] = { ...item, week, city }
        acc.cities[city][week] = { ...item, week, city }
        return acc
      },
      { weeks, cities },
    )

    const evolutionOverTheTimeChartData = {
      coordinateX: Object.keys(cityWeekMatrix.weeks).sort(),
      coordinateY: Object.entries(cities).map(([key, value]) => ({
        name: key,
        data: Object.entries(value)
          .map(([key, value]) => formatItemAndConvertCVRToPercent(value))
          .sort((a, b) => {
            if (a.week > b.week) {
              return 1
            }
            if (a.week < b.week) {
              return -1
            }
            return 0
          }),
      })),
    }

    const evolutionPerCityChartData = {
      coordinateX: Object.keys(cityWeekMatrix.cities).sort(),
      coordinateY: Object.entries(weeks).map(([key, value]) => ({
        name: key,
        data: Object.entries(value)
          .map(([key, value]) => formatItemAndConvertCVRToPercent(value))
          .sort((a, b) => {
            if (a.city > b.city) {
              return 1
            }
            if (a.city < b.city) {
              return -1
            }
            return 0
          }),
      })),
    }

    const chartMetrics = (() => {
      const chartMetrics = { ...chartData[0] }
      delete chartMetrics.chartId
      return Object.keys(chartMetrics)
    })()

    const chartWeeks = Object.keys(weeks).sort()
    const chartDataHash = Object.fromEntries(
      chartData.map(item => [[item.chartId], { ...item }]),
    )

    return {
      evolutionOverTheTimeChartData,
      evolutionPerCityChartData,
      chartDataHash,
      chartMetrics,
      chartWeeks,
    }
  }, [completedMissions, metrics])

  let maxElapsed = 0
  let newestConversionTimestamp = null
  const conversionsOverTime = missions.map(mission => {
    const dto = []
    mission.conversions.nodes.forEach(conversion => {
      const conversionTimestamp = new Date(conversion.timestamp)
      const timeElapsed = conversionTimestamp - new Date(mission.dateTime)
      // Round up the timeElapsed to the nearest period
      const splitPeriod = 1000 * 60 * 60 * 24
      const daysElapsed = Math.ceil(timeElapsed / splitPeriod)
      if (daysElapsed > maxElapsed) maxElapsed = daysElapsed
      if (!dto[daysElapsed]) dto[daysElapsed] = 0
      dto[daysElapsed]++
      if (conversionTimestamp > newestConversionTimestamp) {
        newestConversionTimestamp = conversionTimestamp
      }
    })
    return dto
  })

  // Iterate through all the conversions over time and make them cumulative
  // And also standardise the array lengths, based on the maxIndex prop
  // So at the moment we may have
  // ```
  // // maxIndex = 4
  // [
  //   [ undefined, 2, undefined, 1 ],
  //   [ 1, undefined, 1 ],
  // ]
  // ```
  // The above will end up being
  // ```
  // [
  //   [ 0, 2, 2, 3, 3 ],
  //   [ 1, 1, 2, 2, 2 ],
  // ]
  // ```

  // Normalise the arrays
  conversionsOverTime.forEach(arr => zeroOutMissingValues(arr))
  // Make them cumulative
  conversionsOverTime.forEach(makeValuesCumulative)

  const conversionData = {
    legend: [
      { label: 'Days Elapsed', key: 'daysElapsed' },
      { label: 'Total Conversions', key: 'totalConversions' },
      { label: 'Min Total Conversions', key: 'minTotalConversions' },
    ],
    data: [],
  }

  const conversionCountData = countArrayValues(conversionsOverTime)
  const maxSampleSize = conversionCountData[0]

  // Filter out small sample sizes
  conversionCountData.forEach((sampleSize, i) => {
    // If the sample size is less than 20% of our best sample size, don't
    // display it
    if (sampleSize < maxSampleSize * 0.2) delete conversionCountData[i]
  })
  const conversionObjData = averageArrayValues(conversionsOverTime)
  for (let day in conversionObjData) {
    if (conversionCountData[day] !== undefined)
      conversionData.data.push({
        daysElapsed: Number(day),
        totalConversions: conversionObjData[day],
      })
  }

  const conversionDataAxisKeys = [
    'daysElapsed',
    'totalConversions' /*, 'minTotalConversions'*/,
  ]

  return {
    bulletCharts,
    cityTableData,
    tagTableData,
    weeksTableData,
    evolutionOverTheTimeChartData,
    evolutionPerCityChartData,
    chartMetrics,
    chartWeeks,
    chartDataHash,
    metrics,
    missions,
    missionCost: missions.reduce((acc, mission) => {
      return acc + Number(mission.distributionCost)
    }, 0),
    totalFlyersDistributed,
    totalWeeks,
    conversionData,
    conversionDataAxisKeys,
    newestConversionTimestamp,
  }
}

export const filterCompletedMissions = mission =>
  [
    MISSION_STATUS_ENUM.COMPLETED,
    MISSION_STATUS_ENUM.UNDER_REVIEW,
    MISSION_STATUS_ENUM.PAID,
  ].includes(mission.status) && !mission.cancelled



// WEBPACK FOOTER //
// ./src/Containers/StatsOverview/util.js